import React, { useEffect, useState } from 'react';
import {
  LineChart, Line, BarChart, Bar, AreaChart, Area,
  ComposedChart, RadarChart, Radar, PolarGrid, PolarAngleAxis, PolarRadiusAxis,
  XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend
} from 'recharts';
import { API_BASE } from '../../config';
import { useAuth } from '../../context/AuthContext';
import './UsageTrendsTab.css';

const UsageTrendsTab = () => {
  const { token } = useAuth();
  const [usageData, setUsageData] = useState([]);
  const [group, setGroup] = useState('day');
  const [chartType, setChartType] = useState('line');
  const [tenantId, setTenantId] = useState('');
  const [tenants, setTenants] = useState([]);
  
  const now = new Date();
  const oneWeekAgo = new Date();
  oneWeekAgo.setDate(now.getDate() - 7);

  const toDateTimeLocal = (date) => {
  const pad = (n) => String(n).padStart(2, '0');

  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
  };

  const [from, setFrom] = useState(toDateTimeLocal(oneWeekAgo));
  const [to, setTo] = useState(toDateTimeLocal(now));
  // Fetch list of tenants
  const fetchTenants = async () => {
    try {
      const res = await fetch(`${API_BASE}/admin/tenants`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      const data = await res.json();

      setTenants((data.content || []).map(t => ({ ...t, id: Number(t.id) })));

    } catch (err) {
      console.error('Error fetching tenants:', err);
      setTenants([]);
    }
  };

  // Fetch usage stats for a selected tenant or all
  const fetchUsage = async () => {
    if (!from || !to) return;

    const params = new URLSearchParams({ from, to, group });
    if (tenantId) params.append('tenantId', tenantId);

    try {
      const res = await fetch(`${API_BASE}/admin/stats/usage?${params}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      setUsageData(data);
    } catch (err) {
      console.error('Error fetching usage data:', err);
      setUsageData([]);
    }
  };

  useEffect(() => {
    if (token) fetchTenants();
  }, [token]);

  useEffect(() => {
    if (token && from && to) fetchUsage();
  }, [from, to, group, tenantId, token]);

  const chartTypes = ['line', 'bar', 'area', 'composed', 'radar'];

  const renderChart = () => {
    if (chartType === 'radar') {
      const radarData = usageData.map((d) => ({
        date: d.date,
        Tokenize: d.tokenize,
        Detokenize: d.detokenize,
      }));

      return (
        <ResponsiveContainer width="100%" height={350}>
          <RadarChart data={radarData}>
            <PolarGrid />
            <PolarAngleAxis dataKey="date" />
            <PolarRadiusAxis />
            <Radar name="Tokenize" dataKey="Tokenize" stroke="#2363eb" fill="#2363eb" fillOpacity={0.6} />
            <Radar name="Detokenize" dataKey="Detokenize" stroke="#82ca9d" fill="#82ca9d" fillOpacity={0.6} />
            <Legend />
            <Tooltip />
          </RadarChart>
        </ResponsiveContainer>
      );
    }

    const sharedProps = {
      data: usageData,
      margin: { top: 20, right: 30, left: 20, bottom: 5 }
    };

    switch (chartType) {
      case 'bar':
        return (
          <ResponsiveContainer width="100%" height={350}>
            <BarChart {...sharedProps}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="date" />
              <YAxis />
              <Tooltip />
              <Legend />
              <Bar dataKey="tokenize" fill="#2363eb" />
              <Bar dataKey="detokenize" fill="#82ca9d" />
            </BarChart>
          </ResponsiveContainer>
        );
      case 'line':
        return (
          <ResponsiveContainer width="100%" height={350}>
            <LineChart {...sharedProps}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="date" />
              <YAxis />
              <Tooltip />
              <Legend />
              <Line type="monotone" dataKey="tokenize" stroke="#2363eb" />
              <Line type="monotone" dataKey="detokenize" stroke="#82ca9d" />
            </LineChart>
          </ResponsiveContainer>
        );
      case 'area':
        return (
          <ResponsiveContainer width="100%" height={350}>
            <AreaChart {...sharedProps}>
              <defs>
                <linearGradient id="colorTokenize" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#2363eb" stopOpacity={0.8} />
                  <stop offset="95%" stopColor="#2363eb" stopOpacity={0} />
                </linearGradient>
                <linearGradient id="colorDetokenize" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#82ca9d" stopOpacity={0.8} />
                  <stop offset="95%" stopColor="#82ca9d" stopOpacity={0} />
                </linearGradient>
              </defs>
              <XAxis dataKey="date" />
              <YAxis />
              <CartesianGrid strokeDasharray="3 3" />
              <Tooltip />
              <Legend />
              <Area type="monotone" dataKey="tokenize" stroke="#2363eb" fillOpacity={1} fill="url(#colorTokenize)" />
              <Area type="monotone" dataKey="detokenize" stroke="#82ca9d" fillOpacity={1} fill="url(#colorDetokenize)" />
            </AreaChart>
          </ResponsiveContainer>
        );
      case 'composed':
        return (
          <ResponsiveContainer width="100%" height={350}>
            <ComposedChart {...sharedProps}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="date" />
              <YAxis />
              <Tooltip />
              <Legend />
              <Bar dataKey="tokenize" barSize={20} fill="#2363eb" />
              <Line type="monotone" dataKey="detokenize" stroke="#82ca9d" />
            </ComposedChart>
          </ResponsiveContainer>
        );
      default:
        return <p>Unsupported chart type.</p>;
    }
  };

  return (
<section className="analytics-section">
  <header className="section-header">
    <h3>Token Usage Trends</h3>
    <span className="section-subtitle">Tokenize vs Detokenize activity</span>
  </header>

  <div className="chart-filters">
    <div className="filter-group">
      <label htmlFor="from-date">From</label>
      <input
        id="from-date"
        type="datetime-local"
        max={to}
        value={from}
        onChange={e => setFrom(e.target.value)}
        className="input"
      />
    </div>

    <div className="filter-group">
      <label htmlFor="to-date">To</label>
        <input
          id="to-date"
          type="datetime-local"
          min={from}
          value={to}
          onChange={e => setTo(e.target.value)}
          className="input"
        />
    </div>

    <div className="filter-group">
      <label htmlFor="tenant-select">Tenant</label>
      <select
        id="tenant-select"
        value={tenantId}
        onChange={e => setTenantId(e.target.value)}
        className="input"
      >
        <option value="">All Tenants</option>
        {tenants.map(t => (
          <option key={t.id} value={t.id}>
            {t.email || t.name || t.id}
          </option>
        ))}
      </select>
    </div>

    <div className="filter-group">
      <label htmlFor="group-select">Group By</label>
      <select
        id="group-select"
        value={group}
        onChange={e => setGroup(e.target.value)}
        className="input"
      >
        <option value="day">Daily</option>
        <option value="week">Weekly</option>
        <option value="month">Monthly</option>
        <option value="year">Yearly</option>
      </select>
    </div>

    <div className="filter-group">
      <label htmlFor="chart-type-select">Chart</label>
      <select
        id="chart-type-select"
        value={chartType}
        onChange={e => setChartType(e.target.value)}
        className="input"
      >
        {chartTypes.map(type => (
          <option key={type} value={type}>
            {type.toUpperCase()}
          </option>
        ))}
      </select>
    </div>
  </div>

  <div className="chart-container">
    {usageData.length === 0 ? (
      <p style={{ color: 'var(--text-secondary)', textAlign: 'center' }}>
        No data available for the selected period.
      </p>
    ) : (
      renderChart()
    )}
  </div>
</section>

);
};

export default UsageTrendsTab;
